import { useState } from "react";
import Markdown from "markdown-to-jsx";
import classNames from "classnames";
import { AnimatePresence, m } from "framer-motion";

import Button from "components/buttonNew/button";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";

type TitleDescription = {
  singleLine: string;
  multilineText: string;
};

export const SubHeading = ({ data }) => {
  const { track } = useMixpanelTracking();
  const [isSubHeroExpanded, setIsSubHeroExpanded] = useState<boolean>(false);
  const textStyling = {
    "max-md:text-left": isSubHeroExpanded,
    "max-md:text-center": !isSubHeroExpanded,
  };

  const variants = {
    enter: {
      height: "auto",
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
    exit: {
      height: 0,
      transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
  };

  const variantsChild = {
    enter: {
      opacity: 1,
      transition: { staggerChildren: 0.5, delayChildren: 0.2, duration: 0.7 },
    },
    exit: {
      opacity: 0,
      transition: {
        staggerChildren: 0.05,
        staggerDirection: -1,
        duration: 0.05,
      },
    },
  };

  const { allPoints, proPoints } = data;
  const isPoints = [...allPoints, ...proPoints].length > 0;

  function handleClick() {
    track("Click learn more button", {
      "Click text": isSubHeroExpanded
        ? data.subHeroHideButtonText
        : data.subHeroShowButtonText,
    });
    setIsSubHeroExpanded(!isSubHeroExpanded);
  }

  return (
    <>
      <div className="mt-[70px] flex flex-col md:flex-row">
        <div className="flex-1 border-dashed border-mono-20 px-0 md:border-r-2 md:pr-5 lg:px-[45px]">
          <h2
            className={classNames(
              "text-center font-recklessBold text-[18px] leading-[130%] text-cerise md:mt-5 md:text-[26px] lg:text-[32px]",
              textStyling
            )}
          >
            {data.allHeading}
          </h2>
          <p
            className={classNames(
              "mt-[8px] text-center text-[16px] leading-[130%] text-black-rock md:mt-[18px] md:text-[18px] lg:mt-[10px]",
              textStyling
            )}
          >
            {data.allSubheading}
          </p>

          {allPoints.length > 0 ? (
            <AnimatePresence initial={false}>
              {isSubHeroExpanded ? (
                <m.div
                  key="all-main"
                  className="mt-10 space-y-[20px]"
                  layout
                  variants={variants}
                  initial="exit"
                  animate="enter"
                  exit="exit"
                >
                  {data.allPoints.map((point: TitleDescription, i: number) => {
                    return (
                      <m.div
                        layout
                        variants={variantsChild}
                        key={`all-${i}`}
                        className="border-l-2 border-black-rock pl-[14px]"
                      >
                        <p className="font-bold text-[18px]">
                          {point.singleLine}
                        </p>
                        <p className="text-[16px]">{point.multilineText}</p>
                      </m.div>
                    );
                  })}
                </m.div>
              ) : null}
            </AnimatePresence>
          ) : null}
        </div>

        {isSubHeroExpanded ? (
          <div className="mt-5 flex flex-col px-[0px] md:hidden">
            <div className="h-5" />
            <div className="h-5 border-t-2 border-dashed border-mono-20"></div>
          </div>
        ) : null}

        <div className="mt-5 flex-1 px-0 md:my-5 md:pl-5 lg:px-[45px]">
          <h2
            className={classNames(
              "text-center font-recklessBold text-[18px] leading-[130%] text-pro-blue md:text-[26px] lg:text-[32px]",
              textStyling
            )}
          >
            {data.proHeading}
          </h2>
          <p
            className={classNames(
              "mt-[8px] text-center text-[16px] leading-[130%] text-black-rock md:mt-[18px] md:text-[18px] lg:mt-[10px]",
              textStyling
            )}
          >
            {data.proSubheading}
          </p>

          {proPoints.length > 0 ? (
            <AnimatePresence initial={false}>
              {isSubHeroExpanded ? (
                <m.div
                  key="pro-main"
                  layout
                  variants={variants}
                  initial="exit"
                  animate="enter"
                  exit="exit"
                  className="mt-10 space-y-5 lg:space-y-[34px]"
                >
                  {data.proPoints.map((point: TitleDescription, i: number) => {
                    return (
                      <m.div
                        layout
                        variants={variantsChild}
                        key={`pro-${i}`}
                        className="border-l-2 border-black-rock pl-[14px]"
                      >
                        <p className="font-bold text-[18px]">
                          {point.singleLine}
                        </p>
                        <div className="text-[16px] [&_li]:ml-4 [&_li]:list-outside [&_li]:list-disc">
                          <Markdown>{point.multilineText}</Markdown>
                        </div>
                      </m.div>
                    );
                  })}

                  {data.proAccentSection?.singleLine &&
                  data.proAccentSection?.multilineText ? (
                    <m.div
                      key="pro-info"
                      layout
                      variants={variantsChild}
                      className="space-y-[10px] bg-[rgba(51,80,219,0.1)] px-3 py-4 text-[16px]"
                    >
                      <p className="font-bold text-pro-blue">
                        {data.proAccentSection.singleLine}
                      </p>
                      <p className="">{data.proAccentSection.multilineText}</p>
                    </m.div>
                  ) : null}
                </m.div>
              ) : null}
            </AnimatePresence>
          ) : null}
        </div>
      </div>

      <div
        className={classNames("flex justify-center", {
          "mt-[30px]": isPoints,
        })}
      >
        {isPoints ? (
          isSubHeroExpanded ? (
            <div
              onClick={handleClick}
              className="flex cursor-pointer items-center font-bold text-[16px]"
            >
              <span>{data.subHeroHideButtonText}</span>
              <span className="ml-[8px]">
                <svg
                  width="18"
                  height="11"
                  viewBox="0 0 18 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M1 10L9 2L17 10" stroke="#020231" stroke-width="2" />
                </svg>
              </span>
            </div>
          ) : (
            <Button
              size={"small"}
              colour="black"
              buttonType="secondary"
              onClick={handleClick}
            >
              {data.subHeroShowButtonText}
            </Button>
          )
        ) : null}
      </div>
    </>
  );
};
